import MuiArrowBack from '@mui/icons-material/ArrowBack';
import MuiSearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import ContactFormDialog from '@/components/ContactFormDialog/ContactFormDialog';
import { ProductHitContent } from '@/components/RentalRequestFlow/AutocompleteRow';
import { buildFreeTextRequest } from '@/components/RentalRequestFlow/buildFreeTextRequest';
import DropdownEmptyState from '@/components/RentalRequestFlow/DropdownEmptyState';
import { searchLabelProps } from '@/components/RentalRequestFlow/SearchInput';
import { RENTAL_PRODUCTS_PATH } from '@/helpers/constants';
import {
  trackHeroContactFormOpen,
  trackHeroSearchSelectFreetext,
  trackHeroSearchSelectProduct,
  trackHeroSearchZeroResults,
} from '@/helpers/heroSearchEvents';
import { useBasketContext } from '@/hooks/useBasketContext';
import useFormatMessage from '@/hooks/useFormatMessage';
import { useSearch } from '@/hooks/useSearch';

import { ListItem, Paper, Popper, SearchInputWrapper } from './SearchBox.style';
import SearchListbox from './SearchListbox/SearchListbox';

const rentalProductsPath = RENTAL_PRODUCTS_PATH;
const SURFACE = 'header';

function SearchBox({ onClose = () => {} }) {
  const { searchQuery, setSearchQuery, searchResults, results } = useSearch();

  const [inputValue, setInputValue] = useState(searchQuery);
  const [contactOpen, setContactOpen] = useState(false);
  const [focused, setFocused] = useState(true);

  // The index returns only hits, so compare the query that produced them.
  const noResults =
    Boolean(inputValue.trim()) &&
    results?._state?.query === inputValue &&
    searchResults.length === 0;

  useEffect(() => {
    if (noResults) trackHeroSearchZeroResults(SURFACE, { query: inputValue });
  }, [noResults, inputValue]);

  const { addRequestToBasket } = useBasketContext();

  const router = useRouter();
  const formatMessage = useFormatMessage();

  const handleSelectOption = useCallback(() => {
    setSearchQuery('');
    setInputValue('');
    onClose();
  }, [setSearchQuery, onClose]);

  const handleInputChange = useCallback(
    (_, value, reason) => {
      if (reason === 'reset') return;
      setSearchQuery(value);
      setInputValue(value);
    },
    [setSearchQuery]
  );

  const handleProductChange = useCallback(
    (_, value, reason) => {
      if (reason === 'selectOption' && value) {
        trackHeroSearchSelectProduct(SURFACE, {
          query: inputValue,
          productSlug: value.slug ?? null,
          position: searchResults.indexOf(value),
          section: 'products',
        });
        handleSelectOption();
        router.push(`${rentalProductsPath}/${value.slug}`);
      }
    },
    [handleSelectOption, router, inputValue, searchResults]
  );

  const handleRequestFreeText = useCallback(
    async query => {
      trackHeroSearchSelectFreetext(SURFACE, { query, source: 'zero_results' });
      await addRequestToBasket(buildFreeTextRequest(query));
      handleSelectOption();
      router.push('/basket');
    },
    [addRequestToBasket, handleSelectOption, router]
  );

  return (
    <>
      <Autocomplete
        inputValue={inputValue}
        freeSolo
        onInputChange={handleInputChange}
        options={searchResults}
        open
        getOptionLabel={option => option.title ?? ''}
        onChange={handleProductChange}
        renderInput={params => (
          <SearchInputWrapper>
            <IconButton
              data-test-subject="close-button"
              onClick={onClose}
              size="small"
              sx={{ borderRadius: '50%' }}>
              <MuiArrowBack />
            </IconButton>
            <TextField
              data-test-subject="search-input"
              {...params}
              label={formatMessage('search_dialog_input_label')}
              InputLabelProps={searchLabelProps(focused || Boolean(inputValue))}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              variant="outlined"
              size="medium"
              InputProps={{
                ...params.InputProps,
                autoFocus: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <MuiSearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </SearchInputWrapper>
        )}
        slotProps={{
          paper: { elevation: 0 },
          popper: {
            disablePortal: true,
            style: {
              position: 'relative',
            },
            modifiers: [
              {
                name: 'applyStyles',
                enabled: false,
              },
            ],
          },
        }}
        renderOption={(props, option) => {
          return (
            <Link
              key={option.objectID}
              href={`${rentalProductsPath}/${option.slug}`}
              onClick={handleSelectOption}>
              <ListItem data-test-subject="search-element" {...props}>
                <ProductHitContent
                  hit={option}
                  primary={option.name}
                  secondary={option.category_name}
                />
              </ListItem>
            </Link>
          );
        }}
        ListboxComponent={SearchListbox}
        ListboxProps={{
          onClose,
          sx: {
            // 89px is the mobile input bar, so only the list scrolls.
            maxHeight: {
              xs: 'calc(100dvh - 89px)',
              md: '60vh',
            },
            '& .MuiAutocomplete-option': { p: 0 },
            '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: 'action.hover',
            },
          },
        }}
        PopperComponent={Popper}
        PaperComponent={Paper}
        filterOptions={x => x}
        fullWidth
        clearText={formatMessage('rental_request_flow_clear_aria')}
        sx={{ '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' } }}
      />
      {noResults && (
        <Paper elevation={0} sx={{ mt: { md: 1 } }}>
          <DropdownEmptyState
            query={inputValue}
            variant="zero_results"
            onRequestFreeText={handleRequestFreeText}
            onOpenContact={() => {
              trackHeroContactFormOpen(SURFACE, { source: 'zero_results' });
              setContactOpen(true);
            }}
          />
        </Paper>
      )}
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </>
  );
}

export default SearchBox;
